import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { QuizAnswers, QuizContextState } from "@/pages/quiz/context/types";
import {
  getInitialAnswers,
  getQuizResultPageUrl,
  mapBlockTree,
  questionNamesForAnswersToBeTransformed,
} from "@/pages/quiz/context/common";
import { getSelectedOptionIds } from "@/pages/quiz/context/utils";
import { CustomFormBlock, PostAnswerParams } from "@/types";
import { submitQuizAnswer } from "@/services/quizServices";

import { getSessionId } from "@/utils/getSessionId";

const DEFAULT_STATE: QuizContextState = {
  questions: null,
  initialQuestions: [],
  answers: {},
  selectedQuestionId: null,
  selectedQuestionIdx: -1,
  selectedQuestion: null,
  isLoadingAnswer: false,
  isPrerenderEnabled: false,
  isShowSubmitButton: false,
  voucherId: null,
  quizId: null,
  loadQuizBlocksErrorMessage: null,
  onFinishQuiz: () => {},
  onChangeQuestionValue: () => {},
  onAnswer: () => new Promise(() => {}),
  onBack: () => {},
  setIsShowSubmitButton: () => {},
};

const QuizContext = createContext(DEFAULT_STATE);

interface IProps {
  children: JSX.Element;
  questions: CustomFormBlock[] | null;
  quizId?: string;
  voucherId?: string | null;
  isSubmitOnLastAnswer?: boolean;
  isCommerce?: boolean;
  monthlyCheckInId?: string | null;
  loadQuizBlocksErrorMessage?: string | null;
}

const QuizContextProvider = ({
  children,
  questions: initialQuestions,
  quizId,
  voucherId = null,
  isSubmitOnLastAnswer = true,
  isCommerce = false,
  monthlyCheckInId = null,
  loadQuizBlocksErrorMessage = null,
}: IProps) => {
  const [selectedQuestionId, setSelectionQuestionId] = useState<string | null>(
    null
  );
  const [isLoadingAnswer, setLoadingAnswer] = useState(false);
  const [isPrerenderEnabled, setPrerenderEnabled] = useState(false);
  const [isShowSubmitButton, setIsShowSubmitButton] = useState(true);

  // need to recalculate conditionals on each answer
  const [questions, setQuestions] = useState<CustomFormBlock[]>([]);

  const [answers, setAnswers] = useState<QuizContextState["answers"]>({});

  const selectedQuestionIdx =
    questions?.findIndex((question) => question.id === selectedQuestionId) ??
    -1;

  const selectedQuestion = questions?.[selectedQuestionIdx] || null;

  const validUserId = quizId ? getSessionId() : voucherId;

  const isLastQuestion = selectedQuestionIdx >= questions.length - 1;
  const isRefreshOnLastAnswer =
    !!selectedQuestion?.attributes?.sermorelinAgentRerfresh;

  // questions with negative order shouldn't be displayed by default.
  // They usually used as conditionals or additional trigger
  const initialQuestionsMapped =
    initialQuestions
      ?.filter?.((question) => question.order >= 0)
      .filter?.(
        (question) =>
          !isCommerce ||
          question.name !== "custom-select-treatment-option-bundle"
      ) ?? null;

  useEffect(() => {
    if (!selectedQuestionId && initialQuestionsMapped?.length) {
      const initialAnswers = getInitialAnswers(initialQuestionsMapped);

      const questionsMapped = mapBlockTree(
        initialQuestionsMapped,
        initialAnswers
      );

      const isEveryQuestionsAnswered = questionsMapped.every(
        (question) => question?.is_answered
      );

      const firstUnansweredId = isEveryQuestionsAnswered
        ? questionsMapped[questionsMapped.length - 1]?.id
        : questionsMapped.find((question) => !question.is_answered)?.id ??
          questionsMapped[0]?.id;

      setQuestions(questionsMapped);
      setSelectionQuestionId(firstUnansweredId);
      setAnswers(initialAnswers);
    }
  }, [initialQuestionsMapped, selectedQuestionId]);

  const onFinishQuiz = useCallback(
    (newAnswers: QuizAnswers = answers) => {
      const redirectUrl = getQuizResultPageUrl(newAnswers, quizId);

      window.location.href = redirectUrl;
    },
    [quizId]
  );

  const onNext = useCallback(
    (newAnswers: QuizAnswers) => {
      if (isLastQuestion && isRefreshOnLastAnswer) {
        return window.location.reload();
      }
      if (isLastQuestion && isSubmitOnLastAnswer) {
        return onFinishQuiz(newAnswers);
      }
      // calculate new subtree of conditionals based on answers
      // no need to sue questions instead of initial questions rn
      const questionsMapped = mapBlockTree(
        initialQuestionsMapped ?? [],
        newAnswers
      );

      const nextQuestion = questionsMapped?.[selectedQuestionIdx + 1];

      // move forward
      setIsShowSubmitButton(() => true);
      setAnswers(newAnswers);
      setQuestions(questionsMapped);

      if (!!nextQuestion) {
        setSelectionQuestionId(nextQuestion.id);
      }
    },
    [
      initialQuestionsMapped,
      onFinishQuiz,
      isLastQuestion,
      isRefreshOnLastAnswer,
      selectedQuestionIdx,
      isSubmitOnLastAnswer,
    ]
  );

  const onChangeQuestionValue = useCallback(
    ({
      answer,
      questionId,
      isAnswered = false,
    }: {
      questionId: string;
      answer: any;
      isAnswered?: boolean;
    }) => {
      const targetQuestion = questions.find(
        (question) => question.id === questionId
      );

      setAnswers((answers) => ({
        ...answers,
        [questionId]: {
          value: answer,
          questionId,
          isAnswered: isAnswered,
          blockName: targetQuestion?.name ?? "",
        },
      }));
    },
    [answers, onNext, questions]
  );

  const onAnswer = useCallback(
    async (
      questionId: string,
      answer: any,
      useServerValidation = false,
      skipApi = false
    ) => {
      if (!isPrerenderEnabled) setPrerenderEnabled(true);

      onChangeQuestionValue({ questionId, answer, isAnswered: true });

      try {
        setLoadingAnswer(true);

        const targetQuestions = questions.find(
          (question) => question.id === questionId
        );

        const newAnswers = {
          ...answers,
          [questionId]: {
            value: answer,
            questionId,
            isAnswered: true,
            blockName: targetQuestions?.name ?? "",
          },
        };

        if (skipApi) {
          onNext(newAnswers);
          return {};
        }

        let value = { answer };

        // TODO: discuss with server devs before removing
        if (
          questionNamesForAnswersToBeTransformed.some((questionName) =>
            selectedQuestion.name.startsWith(questionName)
          )
        ) {
          value = answer;
        }

        const optionIds = getSelectedOptionIds(targetQuestions, answer);

        const validSubmitData = quizId
          ? {
              userId: validUserId || "",
              quizId,
            }
          : {
              userId: validUserId || "",
            };

        const answerData: PostAnswerParams = {
          ...validSubmitData,
          id: questionId,
          value,
          option_ids: optionIds,
          ...(monthlyCheckInId && { monthly_check_in_id: monthlyCheckInId }),
        };

        const serverErrors = submitQuizAnswer(
          answerData,
          selectedQuestionIdx + 1
        );

        // case 1: wait for server validation
        // (also before a reload, so the request isn't cancelled)
        if (useServerValidation || (isLastQuestion && isRefreshOnLastAnswer)) {
          const serverErrorsAwaited = await serverErrors;

          if (!serverErrorsAwaited) {
            onNext(newAnswers);
          }

          return {
            serverError: serverErrorsAwaited,
          };
        } else {
          // case 2: skip server validation and switch to next question immediately
          onNext(newAnswers);
        }

        return {};
      } catch (e) {
        console.error(e);
        return {};
      } finally {
        setLoadingAnswer(false);
      }
    },
    [
      answers,
      onChangeQuestionValue,
      onNext,
      questions,
      quizId,
      selectedQuestion?.name,
      selectedQuestionIdx,
      validUserId,
      isPrerenderEnabled,
      initialQuestionsMapped,
      isLastQuestion,
      isRefreshOnLastAnswer,
    ]
  );

  const onBack = useCallback(() => {
    setIsShowSubmitButton(() => true);

    const prevQuestion = questions?.[selectedQuestionIdx - 1];
    const currentQuestion = questions?.[selectedQuestionIdx];

    if (!!prevQuestion) {
      setSelectionQuestionId(prevQuestion.id);
    }
  }, [questions, selectedQuestionIdx]);

  const contextValue = useMemo<QuizContextState>(
    () => ({
      // pass through
      initialQuestions,
      voucherId,
      quizId: quizId || null,
      // stateful
      uploadUserId: validUserId || "",
      questions: questions,
      answers,
      isLoadingAnswer,
      selectedQuestion,
      isPrerenderEnabled,
      loadQuizBlocksErrorMessage,
      // helpers
      selectedQuestionIdx,
      selectedQuestionId,
      isShowSubmitButton,
      // methods
      onChangeQuestionValue,
      onAnswer,
      onBack,
      setSelectionQuestionId,
      setIsShowSubmitButton,
      onFinishQuiz,
    }),
    [
      initialQuestions,
      voucherId,
      quizId,
      questions,
      answers,
      validUserId,
      selectedQuestionId,
      isLoadingAnswer,
      selectedQuestion,
      selectedQuestionIdx,
      isPrerenderEnabled,
      isShowSubmitButton,
      loadQuizBlocksErrorMessage,
      onFinishQuiz,
      onChangeQuestionValue,
      setIsShowSubmitButton,
      onAnswer,
      onBack,
      onFinishQuiz,
    ]
  );

  return (
    <QuizContext.Provider value={contextValue}>{children}</QuizContext.Provider>
  );
};

const useQuizContext = () => useContext(QuizContext);

export { QuizContextProvider, useQuizContext };
